<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard" :preventClosing="processing">
        <template v-slot:header>{{ props.view === null ? 'Save view' : 'Rename view' }}</template>
        <template v-if="props.view === null" v-slot:subheader>
            The filters, the search, the sort, the grouping and the columns, under one name.
        </template>

        <form class="flex flex-col gap-4" @submit.prevent="submit">
            <div>
                <vx-label :for="nameId">Name</vx-label>
                <vx-input
                    :id="nameId"
                    v-model="data.name"
                    data-test="view-name"
                    size="lg"
                    :error="displayFirstError(validation.name)"
                />
            </div>

            <div v-if="props.view === null">
                <vx-label :id="audienceLabelId">Who can see it</vx-label>
                <vx-radio-selection v-model="data.audience" :aria-labelledby="audienceLabelId">
                    <vx-radio-selection-item value="team" data-test="audience-team">
                        <template v-slot:header>Everyone</template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="private" data-test="audience-private">
                        <template v-slot:header>Just me</template>
                    </vx-radio-selection-item>
                </vx-radio-selection>
            </div>
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    type="button"
                    color="muted"
                    size="lg"
                    class="grow lg:w-20 lg:grow-0"
                    :disabled="processing"
                    @click="visible = false"
                >
                    Cancel
                </vx-button>
                <vx-button
                    data-test="save-view"
                    type="button"
                    color="primary"
                    size="lg"
                    class="grow"
                    :loading="processing"
                    @click="submit"
                >
                    {{ props.view === null ? 'Save view' : 'Rename' }}
                    <font-awesome-icon widthAuto :icon="props.view === null ? faFloppyDisk : faPen" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faFloppyDisk, faPen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
} from '@voxie/frontend-components';
import { helpers, required } from '@vuelidate/validators';
import { ref, useId, watch } from 'vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminTeamGroupViewsClient } from '~/services';
import type { KioskTeamGroupView, KioskTeamGroupViewState } from '~/services/adminTeamGroupViewsClient.types';
import { ValidationError } from '~/utils/errors';
import { displayFirstError } from '~/utils/validation';
import {
    KIOSK_TEAM_GROUP_VIEW_MAX_NAME_LENGTH,
    KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE,
    isViewNotFound,
} from './kioskTeamGroupsTableHelpers';

type ViewForm = {
    name: string;
    audience: 'team' | 'private';
};

const props = defineProps<{
    teamId: number;
    view: KioskTeamGroupView | null;
    viewState: KioskTeamGroupViewState;
}>();

const emit = defineEmits<{
    created: [view: KioskTeamGroupView];
    renamed: [view: KioskTeamGroupView];
    unavailable: [view: KioskTeamGroupView];
}>();

const visible = defineModel<boolean>('visible', { default: false });

const nameId = useId();
const audienceLabelId = useId();

const filledForm = (): ViewForm => ({ name: props.view?.name ?? '', audience: 'team' });

const data = ref<ViewForm>(filledForm());

const createView = async () => {
    const { data: view } = await adminTeamGroupViewsClient.createView(props.teamId, {
        name: data.value.name,
        share: data.value.audience === 'team',
        state: props.viewState,
    });

    emit('created', view);
};

const renameView = async (view: KioskTeamGroupView) => {
    try {
        const { data: renamed } = await adminTeamGroupViewsClient.updateView(props.teamId, view.id, {
            name: data.value.name,
        });

        emit('renamed', renamed);
    } catch (error) {
        if (!isViewNotFound(error)) {
            throw error;
        }

        visible.value = false;
        emit('unavailable', view);

        throw new ValidationError(KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE);
    }
};

const maxNameLength = helpers.withMessage(
    `The maximum length allowed is ${KIOSK_TEAM_GROUP_VIEW_MAX_NAME_LENGTH}`,
    (value: string) => Array.from(value).length <= KIOSK_TEAM_GROUP_VIEW_MAX_NAME_LENGTH
);

const { submit, processing, validation } = useFormSubmission({
    data,
    validation: { name: { required, maxLength: maxNameLength } },
    submit: async () => {
        await (props.view === null ? createView() : renameView(props.view));

        visible.value = false;
    },
    notification: () => ({ message: props.view === null ? 'View saved.' : 'View renamed.' }),
    preserveScroll: true,
});

watch(visible, (isVisible) => {
    if (isVisible) {
        data.value = filledForm();
        validation.value.$reset();
    }
});
</script>
